<template>
  <div
    class="radio-group"
    :class="`radio-group--size-${size}`"
    v-bind="$attrs"
  >
    <div
      v-if="$slots['label']"
      :id="radioGroupLabelId"
      class="radio-group__label"
    >
      <slot name="label" />
    </div>
    <div
      :aria-labelledby="$slots['label'] ? undefined : radioGroupLabelId"
      class="radio-group__items"
      role="radiogroup"
    >
      <button
        v-for="(option, idx) of options"
        :key="option.id"
        role="radio"
        class="radio-group__items__item"
        :data-test="`radio-option-${idx}`"
        :class="{
          'radio-group__items__item--active': option.id === props.value,
        }"
        :aria-checked="option.id === props.value"
        :disabled="option.disabled"
        @click="onClick(option)"
      >
        <slot
          name="option"
          :option="option"
        >
          {{ option.text }}
        </slot>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts" generic="T extends OptionType">
import type { PropType } from 'vue'
import { computed } from 'vue'
import type { OptionType } from '@/uiKit/types'
import { buttonProps } from '@/uiKit/Button/props'

const props = // eslint-disable-next-line vue/define-props-declaration
  defineProps({
    // available options
    options: { required: true, type: Array as PropType<T[]> },
    // ID of the currently selected option.
    value: { default: null, type: String as PropType<string | null> },
    // ID to apply to the root element. Is required for accessibility.
    id: { default: null, type: String as PropType<string | null> },
    size: buttonProps.size,
  })

const emit = defineEmits<{ (e: 'change', value: T['id']): void }>()

/**
 * For accessibility, the element with role='radiogroup' needs to be passed
 * a reference to the ID of its label.
 */
const radioGroupLabelId = computed<string>(() => `${props.id}-label`)

const onClick = (chosenOption: T): void => emit('change', chosenOption.id)
</script>

<style lang="scss" scoped>
@import '@/uiKit/assets/borders.scss';
@import '@/uiKit/assets/colors.scss';
@import '@/uiKit/assets/fonts.scss';
@import '@/uiKit/assets/heights.scss';
@import '@/uiKit/assets/shadows.scss';
@import '@/uiKit/assets/spacings.scss';

$wrapper-border-radius: $borderRadius8;
$wrapper-padding: $spacing-1;
$button-border-radius: $wrapper-border-radius - $wrapper-padding;

.radio-group {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: row;

  &--size- {
    &xs {
      height: $h-xs;

      .radio-group__items {
        border-radius: $borderRadius8;
        padding: $spacing-1;

        &__item {
          border-radius: $borderRadius6;
        }
      }
    }

    &sm {
      height: $h-sm;
    }

    &md {
      height: $h-md;
    }

    &lg {
      height: $h-lg;
    }
  }

  &__label {
    @include typographyRegularBody200;
    color: var(--color-text-subtle);
    flex-grow: 1;
  }

  &__items {
    background-color: var(--color-background-gray-subtlest);
    gap: $spacing-2;
    height: 100%;
    display: flex;
    border-radius: $borderRadius8;
    padding: $spacing-2;
    flex-grow: 1;

    &__item {
      @include typographyRegularBody200;
      flex: 1 0 0;
      padding: 0;
      white-space: nowrap;
      background-color: transparent;
      border-radius: $borderRadius4;
      color: var(--color-text-subtlest);

      &:deep(.icon-button) {
        height: $h-icon;
        padding: 0;
      }

      &:hover {
        outline: 0;
        background-color: var(--color-background-gray-subtlest);
        &:not([disabled]) {
          color: var(--color-text-subtle);
        }
      }

      &:disabled {
        color: var(--color-text-disabled);
      }

      &--active {
        color: var(--color-text-default);
        background-color: var(--color-surface-default-persist);
        box-shadow: $boxShadowXS;

        &:hover {
          color: var(--color-text-default);
          background-color: var(--color-surface-default);
        }
      }
    }
  }
}
</style>
